import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { supabase, createTimelineEvent } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Upload, FileText, Download, Folder, File, Image, Trash2, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

// --- Upload hardening constants ---
const ALLOWED_MIME_TYPES = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

const ALLOWED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'docx']);
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const SIGNED_URL_EXPIRY = 300; // 5 minutes
const FILE_INPUT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx';

/**
 * Client-side pre-validation (fast feedback before server round-trip).
 * The Edge Function performs the authoritative server-side validation.
 */
const validateFile = (file) => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.has(ext)) {
    return `File type ".${ext || '?'}" is not allowed. Accepted: PDF, PNG, JPG, DOCX.`;
  }
  if (!ALLOWED_MIME_TYPES[file.type]) {
    return `File content type "${file.type}" is not allowed.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds the 10 MB limit.`;
  }
  return null; // valid
};

export const DocumentVault = ({ transactionId, onDocumentUploaded }) => {
  const { user, profile, displayName } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, [transactionId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          uploader:uploaded_by (
            full_name,
            email
          )
        `)
        .eq('transaction_id', transactionId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  // --- Upload with hardening ---
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type, MIME, and size
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      // Generate safe internal path (never trust user filename for storage)
      const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
      const storagePath = `${transactionId}/${crypto.randomUUID()}.${ext}`;

      // Upload to private Supabase Storage bucket
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Create document record in DB (stores original filename for display)
      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          transaction_id: transactionId,
          file_name: file.name,
          file_path: storagePath,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: user.id,
        });

      if (dbError) throw dbError;

      // Audit: timeline event
      await createTimelineEvent(
        transactionId,
        'document_uploaded',
        `Uploaded: ${file.name}`,
        { file_name: file.name, file_size: file.size, mime_type: file.type }
      );

      toast.success("Document uploaded");
      fetchDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || "Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // --- Download via signed URL ---
  const handleDownload = async (doc) => {
    try {
      // Generate short-lived signed URL (5 min)
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(doc.file_path, SIGNED_URL_EXPIRY);

      if (error) throw error;

      // Trigger download via signed URL
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = doc.file_name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Audit: log download event
      await createTimelineEvent(
        transactionId,
        'document_downloaded',
        `Downloaded: ${doc.file_name}`,
        { document_id: doc.id, file_name: doc.file_name }
      );
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error("Failed to download document");
    }
  };

  // --- Delete with audit ---
  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;

    setDeletingId(doc.id);
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([doc.file_path]);

      if (storageError) {
        console.warn('Storage delete warning:', storageError.message);
        // Continue to delete DB record even if storage fails (file may already be gone)
      }

      // Delete DB record
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      // Audit: log deletion
      await createTimelineEvent(
        transactionId,
        'document_deleted',
        `Deleted: ${doc.file_name}`,
        { document_id: doc.id, file_name: doc.file_name, deleted_by: user.id }
      );

      toast.success("Document deleted");
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error("Failed to delete document");
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    return FileText;
  };

  /** Display-only: prettify filename for readability */
  const prettifyFilename = (name) => {
    if (!name) return 'Untitled';
    // Strip extension
    let base = name.replace(/\.[^.]+$/, '');
    // Replace underscores, dashes, and dots with spaces
    base = base.replace(/[_\-.]+/g, ' ');
    // Collapse multiple spaces
    base = base.replace(/\s+/g, ' ').trim();
    // Title-case each word (preserve all-caps words like FINAL)
    base = base.replace(/\b\w+/g, word => {
      if (word === word.toUpperCase() && word.length > 1) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
    return base || 'Untitled';
  };

  /** Extract file extension for secondary display */
  const getExtension = (name) => {
    const match = name?.match(/\.([^.]+)$/);
    return match ? match[1].toUpperCase() : '';
  };

  return (
    <Card className="border-border/60 shadow-soft" data-testid="document-vault">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Folder className="w-4 h-4" />
              Documents
            </CardTitle>
            <CardDescription className="text-sm">
              Private workspace — access limited to transaction participants
            </CardDescription>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_INPUT_ACCEPT}
              className="hidden"
              onChange={handleFileSelect}
              data-testid="file-input"
            />
            <Button 
              className="btn-press rounded-md h-9 text-sm" 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Upload Document</>
              )}
            </Button>
          </div>
        </div>
        {/* Security notice */}
        <div className="flex items-start gap-2 mt-3 p-2.5 rounded-md bg-muted/50 border border-border/40" data-testid="security-notice">
          <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Documents are stored in a private transaction workspace. Access is limited to authenticated transaction participants. Accepted formats: PDF, PNG, JPG, DOCX (max 10 MB).
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              No documents yet. Upload transaction documents to share securely with all parties.
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {documents.map((doc) => {
                const IconComponent = getFileIcon(doc.mime_type);
                const isDeleting = deletingId === doc.id;
                
                return (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between p-3 rounded-md border border-border/60 hover:bg-muted/40 transition-colors group ${isDeleting ? 'opacity-50' : ''}`}
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-md bg-primary/8 flex items-center justify-center shrink-0">
                        <IconComponent className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{prettifyFilename(doc.file_name)}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-semibold uppercase">{getExtension(doc.file_name)}</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>·</span>
                          <span>{doc.uploader?.full_name || 'Unknown'}</span>
                          <span>·</span>
                          <span>{formatDate(doc.created_at)}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground/60 truncate mt-0.5">{doc.file_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDownload(doc)}
                        disabled={isDeleting}
                        title="Download"
                        data-testid={`download-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(doc)}
                        disabled={isDeleting}
                        title="Delete"
                        data-testid={`delete-${doc.id}`}
                      >
                        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
